import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  CommercialOutage,
  CommercialOutageConnection,
  CommercialOutageSearch,
} from 'src/app/model/commercial-services/commercial-outage';
import { isNullOrUndefined, getCorrectedDate } from 'src/app/utils/utils';
import { CommercialFacilityBuilderService } from './commercial-facility-builder.service';
import {
  CommercialOutageMessageKeys,
  ValidationConfig,
} from 'src/app/core/type-helpers/validation-msg';
import { inject } from '@angular/core';
import { CommercialOutageUpdateDto } from 'src/app/model/commercial-services/commercial-outage-update-dto';

@Injectable({
  providedIn: 'root',
})
export class CommercialOutageBuilderService {
  private _commercialFacilityBuilder = inject(CommercialFacilityBuilderService);

  buildCommercialOutageValidationMessages(): ValidationConfig<CommercialOutageMessageKeys> {
    return {
      messages: {
        outageStartDate: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.OUTAGE_START_DATE_REQUIRED',
        },
        outageEndDate: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.OUTAGE_END_DATE_REQUIRED',
        },
        numberOfUnitsOnOutage: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.NUMBER_OF_UNITS_ON_OUTAGE_REQUIRED',
        },
        numberOfTotalUnits: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.NUMBER_OF_TOTAL_UNITS_REQUIRED',
        },
        aorId: {
          required: 'COMMERCIAL_SERVICES.VALIDATION.AOR_ID_REQUIRED',
        },
        commercialFacilityId: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.COMMERCIAL_FACILITY_ID_REQUIRED',
        },
        commercialOperatingAreaId: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.COMMERCIAL_OPERATING_AREA_ID_REQUIRED',
        },
        commercialMarketAreaId: {
          required:
            'COMMERCIAL_SERVICES.VALIDATION.COMMERCIAL_MARKET_AREA_ID_REQUIRED',
        },
      },
      params: {},
    };
  }

  buildForm(
    fb: UntypedFormBuilder,
    outage: CommercialOutage
  ): UntypedFormGroup {
    const validDate: ValidatorFn = (control) =>
      control.value instanceof Date && Number.isFinite(control.value.getTime())
        ? null
        : { required: true };
    const unitValidators = [
      Validators.required,
      Validators.min(0),
      Validators.max(2147483647),
      Validators.pattern(/^\d+$/),
    ];
    return fb.group({
      id: [outage.id],
      commercialFacilityId: [outage.commercialFacilityId, Validators.required],
      aorId: [outage.aorId],
      commercialOperatingAreaId: [outage.commercialOperatingAreaId],
      commercialMarketAreaId: [outage.commercialMarketAreaId],
      outageStartDate: [
        outage.outageStartDate,
        [Validators.required, validDate],
      ],
      outageEndDate: [outage.outageEndDate, [Validators.required, validDate]],
      numberOfUnitsOnOutage: [outage.numberOfUnitsOnOutage, unitValidators],
      numberOfTotalUnits: [outage.numberOfTotalUnits, unitValidators],
      notes: [outage.notes],
      version: [outage.version],
    });
  }

  updateForm(
    form: UntypedFormGroup,
    outage: CommercialOutage,
    writeAccess: boolean
  ): void {
    form.patchValue({
      id: outage.id,
      commercialFacilityId: outage.commercialFacilityId,
      aorId: outage.aorId,
      commercialOperatingAreaId: outage.commercialOperatingAreaId,
      commercialMarketAreaId: outage.commercialMarketAreaId,
      outageStartDate: outage.outageStartDate,
      outageEndDate: outage.outageEndDate,
      numberOfUnitsOnOutage: outage.numberOfUnitsOnOutage,
      numberOfTotalUnits: outage.numberOfTotalUnits,
      notes: outage.notes,
      version: outage.version,
    });
    form.markAsPristine();
    form.markAsUntouched();

    if (!writeAccess) {
      form.disable();
    }
  }

  buildCreateObject(outage: any): CommercialOutageUpdateDto {
    return {
      commercialFacilityId: outage.commercialFacilityId,
      outageStartDate: outage.outageStartDate.toISOString(),
      outageEndDate: outage.outageEndDate.toISOString(),
      numberOfUnitsOnOutage: Number(outage.numberOfUnitsOnOutage),
      numberOfTotalUnits: Number(outage.numberOfTotalUnits),
      notes: outage.notes ?? '',
    };
  }

  buildUpdateStatement(
    outage: CommercialOutage,
    changes: any
  ): CommercialOutageUpdateDto {
    const update: CommercialOutageUpdateDto = {
      expectedVersion: outage.version + 1,
    };
    const values = this.buildCreateObject({ ...outage, ...changes });
    Object.keys(values).forEach((key) => {
      if (changes[key] !== undefined) {
        (update as any)[key] = (values as any)[key];
      }
    });
    return update;
  }

  buildNewCommercialOutage(): CommercialOutage {
    return {
      id: '',
      outageStartDate: null,
      outageEndDate: null,
      numberOfUnitsOnOutage: null,
      numberOfTotalUnits: null,
      commercialFacilityId: null,
      aorId: null,
      commercialOperatingAreaId: null,
      commercialMarketAreaId: null,
      notes: '',
      createdBy: '',
      createdAt: null,
      updater: '',
      updatedAt: null,
      deletedAt: null,
      version: 0,
      CommercialFacility: null,
    } as unknown as CommercialOutage;
  }

  buildCommercialOutage(i: any): CommercialOutage {
    if (isNullOrUndefined(i)) {
      throw new Error(
        'Cannot build a commercial outage from a missing response.'
      );
    }

    const facility = this._commercialFacilityBuilder.buildCommercialFacility(
      i.CommercialFacility
    );

    if (facility === null) {
      throw new Error(`Commercial outage ${i.id} is missing its facility.`);
    }

    if (isNullOrUndefined(i.AOR)) {
      throw new Error(`Commercial outage ${i.id} is missing its AOR.`);
    }

    return {
      id: i.id,
      outageStartDate: getCorrectedDate(i.outageStartDate)!,
      outageEndDate: getCorrectedDate(i.outageEndDate)!,
      numberOfUnitsOnOutage: i.numberOfUnitsOnOutage,
      numberOfTotalUnits: i.numberOfTotalUnits,
      commercialFacilityId: String(facility.id),
      aorId: String(i.aorId),
      commercialOperatingAreaId: String(i.commercialOperatingAreaId),
      commercialMarketAreaId: String(i.commercialMarketAreaId),
      notes: i.notes,
      createdBy: i.createdBy,
      createdAt: i.createdAt,
      updater: i.updater,
      updatedAt: i.updatedAt,
      deletedAt: i.deletedAt,
      version: i.version,
      CommercialFacility: facility,
      aor: i.AOR,
      CommercialOperatingArea: i.CommercialOperatingArea,
      CommercialMarketArea: i.CommercialMarketArea,
    };
  }

  buildCommercialOutageConnection(item: any): CommercialOutageConnection {
    return {
      items: item.items.map(
        (outage: any): CommercialOutageSearch => ({
          id: outage.id,
          version: outage.version,
          outageStartDate: getCorrectedDate(outage.outageStartDate)!,
          outageEndDate: getCorrectedDate(outage.outageEndDate)!,
          numberOfUnitsOnOutage: outage.numberOfUnitsOnOutage,
          numberOfTotalUnits: outage.numberOfTotalUnits,
          facilityName: outage.CommercialFacility?.facilityName ?? '',
          facilityId: outage.CommercialFacility?.facilityId ?? '',
          aor: outage.AOR?.name ?? '',
          commercialOperatingArea: outage.CommercialOperatingArea?.name ?? '',
          commercialMarketArea: outage.CommercialMarketArea?.name ?? '',
        })
      ),
      totalRecords: item.totalRecords,
    };
  }

  buildCommercialOutageQuery(filters: any): Record<string, string> {
    const query: Record<string, string> = {};

    Object.keys(filters).forEach((key) => {
      const filter = filters[key];
      const value = Array.isArray(filter) ? filter[0]?.value : filter?.value;
      if (value !== null && value !== undefined && value !== '') {
        query[key] =
          value instanceof Date ? value.toISOString() : String(value).trim();
      }
    });
    return query;
  }
}
